import { Alert, Button, Group, Loader, Modal, Select, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useOrderNotificationOptions,
  useResendOrderNotification
} from '../../hooks/orders/notifications';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import { showNotification } from '../../utils/Notification/Notification';

type OrderNotificationModalProps = {
  opened: boolean;
  onClose: () => void;
  orderId: string;
  storeId: string;
};

const errorCodes = new Set([
  'ORDER_NOTIFICATION_PERMISSION_REQUIRED',
  'ORDER_NOTIFICATION_NOT_FOUND',
  'ORDER_NOTIFICATION_INVALID_REQUEST',
  'ORDER_NOTIFICATION_UNAVAILABLE',
  'ORDER_NOTIFICATION_LIMIT_REACHED',
  'ORDER_NOTIFICATION_IN_PROGRESS',
  'ORDER_NOTIFICATION_FAILED'
]);

const OrderNotificationModal = ({
  opened,
  onClose,
  orderId,
  storeId
}: OrderNotificationModalProps) => {
  const { t } = useTranslation(['OrdersCardMoreActionMenu', 'FieldName', 'CommonButtons']);
  const canEdit = useCanEditOrders();
  const optionsQuery = useOrderNotificationOptions(orderId, storeId, opened && canEdit);
  const resend = useResendOrderNotification();
  const [channel, setChannel] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [terminalFailures, setTerminalFailures] = useState<Record<string, string>>({});
  const sending = useRef(false);
  // Retain failed attempts when the dialog closes or the operator switches options.
  const attemptKeys = useRef(new Map<string, string>());

  useEffect(() => {
    setChannel(null);
    setStatus(null);
    setError('');
  }, [opened, orderId, storeId]);

  const options = optionsQuery.data?.options || [];
  const channels = [...new Set(options.map((option) => option.channel))];
  const selected = options.find((option) => option.channel === channel && option.status === status);
  const selectedScope = selected
    ? `${storeId}:${orderId}:${selected.channel}:${selected.status}`
    : '';
  const terminalCode = terminalFailures[selectedScope];
  const busy = resend.isPending;
  const ready = Boolean(
    opened &&
      canEdit &&
      orderId &&
      storeId &&
      selected &&
      !terminalCode &&
      !optionsQuery.isFetching &&
      !optionsQuery.isError
  );

  const getErrorCode = (requestError: unknown) => {
    const codes = (requestError as { response?: { data?: { errors?: unknown[] } } })?.response?.data
      ?.errors;
    return codes?.find(
      (value): value is string => typeof value === 'string' && errorCodes.has(value)
    );
  };
  const errorMessage = (requestError: unknown) =>
    t(
      `OrdersCardMoreActionMenu:notificationResend.errors.${
        getErrorCode(requestError) || 'generic'
      }`
    );

  const handleReviewNewResend = () => {
    if (sending.current || !selectedScope) return;
    attemptKeys.current.delete(selectedScope);
    setTerminalFailures((previous) => {
      const next = { ...previous };
      delete next[selectedScope];
      return next;
    });
    setError('');
  };

  const handleClose = () => {
    if (!sending.current) onClose();
  };

  const handleSend = async () => {
    if (!ready || !selected || sending.current) return;
    sending.current = true;
    setError('');
    const scope = `${storeId}:${orderId}:${selected.channel}:${selected.status}`;
    try {
      const idempotencyKey =
        attemptKeys.current.get(scope) ||
        crypto.randomUUID?.() ||
        Array.from(crypto.getRandomValues(new Uint8Array(16)), (value) =>
          value.toString(16).padStart(2, '0')
        ).join('');
      attemptKeys.current.set(scope, idempotencyKey);
      const result = await resend.mutateAsync({
        orderId,
        storeId,
        channel: selected.channel,
        status: selected.status,
        idempotencyKey
      });
      attemptKeys.current.delete(scope);
      showNotification({
        type: 'Success',
        title: t(
          `OrdersCardMoreActionMenu:notificationResend.${
            result.status === 'already_sent' ? 'alreadySent' : 'success'
          }`
        )
      });
      onClose();
    } catch (requestError) {
      const code = getErrorCode(requestError);
      if (code === 'ORDER_NOTIFICATION_FAILED' || code === 'ORDER_NOTIFICATION_LIMIT_REACHED') {
        setTerminalFailures((previous) => ({ ...previous, [scope]: code }));
      }
      setError(errorMessage(requestError));
    } finally {
      sending.current = false;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('OrdersCardMoreActionMenu:notificationResend.title')}
      size="lg"
      centered
      withCloseButton={!busy}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
    >
      <Stack gap="md">
        <Text size="sm">{t('OrdersCardMoreActionMenu:notificationResend.description')}</Text>
        {optionsQuery.isFetching && <Loader size="sm" />}
        {!canEdit && (
          <Alert color="red">
            {t(
              'OrdersCardMoreActionMenu:notificationResend.errors.ORDER_NOTIFICATION_PERMISSION_REQUIRED'
            )}
          </Alert>
        )}
        {optionsQuery.isError && (
          <Alert color="red">
            {errorMessage(optionsQuery.error)}
            <Button variant="subtle" onClick={() => optionsQuery.refetch()} disabled={busy}>
              {t('OrdersCardMoreActionMenu:notificationResend.reload')}
            </Button>
          </Alert>
        )}
        {optionsQuery.isSuccess && !optionsQuery.isFetching && options.length === 0 && (
          <Alert color="yellow">{t('OrdersCardMoreActionMenu:notificationResend.empty')}</Alert>
        )}
        <Select
          label={t('OrdersCardMoreActionMenu:notificationResend.channel')}
          placeholder={t('OrdersCardMoreActionMenu:notificationResend.chooseChannel')}
          data={channels.map((value) => ({
            value,
            label: t(`OrdersCardMoreActionMenu:notificationResend.channels.${value}`)
          }))}
          value={channel}
          onChange={(value) => {
            setChannel(value);
            setStatus(null);
            setError('');
          }}
          disabled={busy || !canEdit || optionsQuery.isFetching || optionsQuery.isError}
        />
        <Select
          label={t('OrdersCardMoreActionMenu:notificationResend.status')}
          placeholder={t('OrdersCardMoreActionMenu:notificationResend.chooseStatus')}
          data={options
            .filter((option) => option.channel === channel)
            .map((option) => ({
              value: option.status,
              label: t(`FieldName:orderStatuses.${option.status}`)
            }))}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setError('');
          }}
          disabled={busy || !canEdit || !channel || optionsQuery.isFetching || optionsQuery.isError}
        />
        {selected && (
          <Text size="sm">{t('OrdersCardMoreActionMenu:notificationResend.confirmation')}</Text>
        )}
        {(terminalCode || error) && (
          <Alert color="red">
            {terminalCode
              ? t(`OrdersCardMoreActionMenu:notificationResend.errors.${terminalCode}`)
              : error}
            {terminalCode && (
              <Button variant="subtle" onClick={handleReviewNewResend} disabled={busy}>
                {t('OrdersCardMoreActionMenu:notificationResend.reviewNew')}
              </Button>
            )}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={busy}>
            {t('CommonButtons:cancel')}
          </Button>
          <Button onClick={handleSend} loading={busy} disabled={!ready || busy}>
            {t('OrdersCardMoreActionMenu:notificationResend.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default OrderNotificationModal;
